import type { ReactNode } from 'react'
import { createContext, useCallback, useMemo, useState } from 'react'
import type { SnackbarOptions } from './Snackbar'
import { Snackbar } from './Snackbar'

type SnackbarMessage = {
  content: ReactNode
  key: string
  options?: SnackbarOptions
}

type SnackbarContextType = {
  showMessage: (message: ReactNode, options?: SnackbarOptions) => void
  hideMessage: () => void
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
)

type SnackbarProviderProps = {
  children: ReactNode
}

// 状態管理ライブラリを使っていない理由は、Redux を使いたくなく、
// また Zustand や Jotai を入れるならばちゃんとした意思決定をしたいため。
export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [message, setMessage] = useState<SnackbarMessage | undefined>(undefined)

  const showMessage = useCallback(
    (content: ReactNode, options?: SnackbarOptions) => {
      setMessage({
        content,
        key: Date.now().toString(),
        options,
      })
    },
    [],
  )

  const hideMessage = useCallback(() => {
    setMessage(undefined)
  }, [])

  const contextValue = useMemo(
    () => ({
      showMessage,
      hideMessage,
    }),
    [showMessage, hideMessage],
  )

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar message={message} onClose={hideMessage} />
    </SnackbarContext.Provider>
  )
}
